import { InputSelectProps } from './InputSelect';
import useGroupsContext from '@contexts/GroupsContext/hook';
import useFeaturePermissionsContext from '@contexts/FeaturePermissionsContext/hook';
import { PermissionFilter, permiteProduto } from '@utils/permissionFilter';
import {
  filtrarComunidadesPorPapel,
  paraComunidadesSelecionaveis,
} from '@utils/comunidadesSelecionaveis';
import { Controller } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
} from '@mui/material';
import { UserCommunity, UserRole } from '@interfaces/auth';
import { twMerge } from 'tailwind-merge';
import { styles } from './styles';
import useToastContext from '@contexts/ToastContext/hook';
import {
  SELECTED_GROUPS_LOCAL_STORAGE_PREFIX,
  getSelectedGroups,
  setSelectedGroups,
} from '@repository/selectedGroups';
import { useFirstRender } from '@hooks/useFirstRender';
import { resolveDefaultCommunityIds } from '@utils/communitiesStatusFilter';
import { CommunitiesSelectionModal } from './CommunitiesSelectionModal';

export type InputProductProps = Omit<InputSelectProps, 'options' | 'type'> & {
  /**
   * Recorte por PAPEL. Continua existindo para os seletores em que o papel É o assunto — "produtos
   * onde sou afiliado", "onde sou indicador" —, que são semântica de negócio e não controle de
   * acesso (§6.3.2).
   */
  userRolesFilter?: UserRole[];
  /**
   * Recorte por PERMISSÃO (§6.3). Quando vem, ele decide sozinho, e a lista parte de TODOS os
   * produtos alcançados — inclusive os de colaboração, que nenhum papel de comissão cobre.
   */
  permissionFilter?: PermissionFilter;
  selectedLocalStorageId: string;
  onChangeCallback?: (ids: number[]) => void;
  onlyActiveGroups?: boolean;
  noRowsMessage?: string;
};

export const InputProduct = ({
  name,
  control,
  onChangeCallback,
  selectedLocalStorageId,
  userRolesFilter,
  permissionFilter,
  onlyActiveGroups,
  noRowsMessage,
  ...restProps
}: InputProductProps) => {
  const [isOpen, setIsOpen] = useState(false);
  /*
    `communities`, e não `groups`: o papel de cada comunidade sai do BALDE em que ela veio, e este é
    o lugar onde o contexto já respondeu isso (#376).

    Partia dos baldes crus, e daí o rótulo saía de `product.userRole` — campo que a API preenche com
    `AFFILIATED` para o balde de colaboração, porque o `UserRole` de lá só tem os quatro papéis de
    comissão. O efeito eram onze telas de relatório escrevendo "Afiliado" ao lado de uma comunidade
    onde a pessoa é colaboradora. O painel já não lia o campo desde a #366; era só este campo que
    faltava.

    A lista é a mesma: `formatCommunities` percorre os mesmos cinco baldes que `produtosAlcancados`,
    deduplicando pelo mesmo id. O que muda é que agora ela vem com TODOS os papéis de cada
    comunidade, em vez do papel do primeiro balde que a continha.
  */
  const { communities } = useGroupsContext();
  const { getUserPermissions } = useFeaturePermissionsContext();

  const alcancadas = permissionFilter
    ? communities.filter((community) =>
        permiteProduto(getUserPermissions, permissionFilter, community.id),
      )
    : filtrarComunidadesPorPapel(communities, userRolesFilter ?? []);

  /*
    O recorte por status é aplicado DEPOIS dos dois ramos, e não dentro de cada um: era duplicado, e
    duplicado é como o ramo por papel e o ramo por permissão divergem sem ninguém ver.
  */
  const allProducts = onlyActiveGroups
    ? alcancadas.filter((community) => community.active)
    : alcancadas;
  const selectedLocalStorageKey = `${SELECTED_GROUPS_LOCAL_STORAGE_PREFIX}-${selectedLocalStorageId}`;

  /**
   * As ativas, e não todas: uma comunidade encerrada meses atrás continua no sistema pelas métricas, e
   * até aqui ela entrava calada em cada número do relatório.
   */
  const defaultProductIds = resolveDefaultCommunityIds(allProducts);

  const selectedLocalStorage = getSelectedGroups(selectedLocalStorageKey);
  if (
    !selectedLocalStorage ||
    selectedLocalStorage.some((id) => !allProducts.find((p) => p.id === id))
  )
    setSelectedGroups(selectedLocalStorageKey, defaultProductIds);

  const initialSelectedIds: UserCommunity['id'][] =
    getSelectedGroups(selectedLocalStorageKey) || [];

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={initialSelectedIds}
      render={({ field }) => {
        const toast = useToastContext();
        const triggerNoneSelectedToast = useCallback(
          (ids: UserCommunity['id'][]) => {
            !ids.length &&
              toast.setContent(
                'Você deve selecionar pelo menos uma comunidade!',
                'warning',
              );
          },
          [],
        );

        useEffect(() => {
          field.onChange(field.value);
        }, []);

        const selectedProducts = allProducts.filter((product) =>
          field.value.includes(product.id),
        );

        const onSubmit = (ids: UserCommunity['id'][]) => {
          triggerNoneSelectedToast(ids);
          field.onChange(ids);
          onChangeCallback?.(ids);
          setSelectedGroups(selectedLocalStorageKey, ids);
          handleClose();
        };

        const isFirstRender = useFirstRender();
        useEffect(() => {
          if (isFirstRender) return;

          onSubmit(defaultProductIds);
        }, [JSON.stringify(userRolesFilter)]);

        return (
          <>
            <FormControl
              variant="outlined"
              className={twMerge(styles, restProps.className)}
              error={restProps.error}
            >
              <InputLabel htmlFor="products">Comunidades</InputLabel>
              <Select
                name="products"
                label="Comunidades"
                // O fakeValue é usado para forçar o label do Mui ao "estado ativo" (para a borda superior do input)
                value="fakeValue"
                displayEmpty
                open={false}
                MenuProps={{ disableScrollLock: true }}
                onClick={() => setIsOpen(true)}
                renderValue={() => {
                  return (
                    <div className="absolute flex items-center -translate-y-1/2 top-1/2 pr-sm max-w-[calc(100%-4.5rem)]">
                      <Chip
                        label={`${selectedProducts.length} ${
                          selectedProducts.length === 1
                            ? 'selecionado'
                            : 'selecionados'
                        }`}
                      />
                    </div>
                  );
                }}
              >
                <option value="fakeValue" />
              </Select>
              <FormHelperText>{restProps.helperText}</FormHelperText>
            </FormControl>

            <CommunitiesSelectionModal
              isOpen={isOpen}
              onClose={handleClose}
              communities={paraComunidadesSelecionaveis(allProducts)}
              selectedIds={field.value}
              onSubmit={onSubmit}
              noRowsMessage={noRowsMessage}
              // A tela já restringiu o campo às ativas; não há status para filtrar.
              hideStatusFilter={onlyActiveGroups}
            />
          </>
        );
      }}
    />
  );
};
